import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { AssessmentId } from "@thalamos/haven-common";

import {
  CHARTED,
  type Charted,
  type HistoryEvent,
} from "../../pages/FullHistory/fullHistory.js";
import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";

/** One assessment's point on every line. */
export type ProgressionPoint = {
  assessmentId: AssessmentId;
  at: Date;
  counts: Readonly<Record<Charted, number>>;
};

/**
 * The widths of the columns either side of the assessments. The chart and the
 * table beneath it share them, so each point sits above its assessment's
 * column.
 */
export const PROGRESSION_COLUMNS = { item: "22%", across: "15%" } as const;

const HEIGHT = 200;
const PLOT = { top: 40, bottom: 12 };
/** The baselines of the two rows event labels are set on. */
const LABEL_ROWS = [12, 25] as const;
const LABEL_FONT_SIZE = 10;
/** About how wide each character of an event's label is at `LABEL_FONT_SIZE`, in bold. */
const LABEL_CHARACTER = 5.6;
/** How close two events' lines may be before they share a label. */
const MERGE_WITHIN = 16;

const SHORT_DATE = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** The width of the element the ref is on, followed as it changes. */
const useWidth = (fallback: number) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);

  useLayoutEffect(() => {
    const element = ref.current;

    if (element === null) {
      return;
    }

    setWidth(element.clientWidth || fallback);
    const observer = new ResizeObserver(([entry]) => {
      if (entry !== undefined && entry.contentRect.width > 0) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [fallback]);

  return { ref, width };
};

/**
 * A whole step between the y axis's ticks, 1, 2 or 5 times a power of ten,
 * giving no more than four steps above zero.
 */
const stepFor = (max: number) => {
  const rough = max / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const nice =
    [1, 2, 5].map((times) => times * power).find((step) => step >= rough) ??
    10 * power;

  return Math.max(1, nice);
};

/** Where an instant falls between the columns of the assessments either side of it. */
const xOfInstant = (
  points: readonly ProgressionPoint[],
  xOf: (index: number) => number,
  at: Date,
): number | undefined => {
  for (const [index, point] of points.entries()) {
    const next = points[index + 1];

    if (point.at.getTime() === at.getTime()) {
      return xOf(index);
    }

    if (next !== undefined && point.at < at && at < next.at) {
      const share =
        (at.getTime() - point.at.getTime()) /
        (next.at.getTime() - point.at.getTime());

      return xOf(index) + share * (xOf(index + 1) - xOf(index));
    }
  }

  return undefined;
};

/** Events close enough to share a line, at the first one's place, and the label they share. */
type Marker = { x: number; label: string };

/** A marker with where its label starts and the row it is set on. */
type Labelled = Marker & { left: number; row: 0 | 1 };

/**
 * Centres each label over its line, kept within the chart, on the top row
 * where it clears the last label there, otherwise on the second, where it
 * may overlap.
 */
const labelled = (markers: readonly Marker[], width: number): Labelled[] => {
  const ends: [number, number] = [-Infinity, -Infinity];

  return markers.map((marker) => {
    const span = marker.label.length * LABEL_CHARACTER;
    const left = Math.min(Math.max(0, marker.x - span / 2), width - span);
    const row = left > ends[0] + 8 ? 0 : 1;

    ends[row] = left + span;
    return { ...marker, left, row };
  });
};

/**
 * How many items the summary showed in each `CHARTED` column at each of
 * `points`, as a line per kind with each point above its assessment's
 * column, what happened between them marked across it, and the counts at
 * `latest` beside it.
 *
 * Hovering, or moving along it with the arrow keys once it has focus, picks
 * out one assessment and gives every line's count at it. The counts are also
 * in a table a screen reader reads in place of the drawing.
 */
export const ProgressionChart = ({
  points,
  events,
  latest,
}: {
  /** Oldest first, one for each column beneath. */
  points: readonly ProgressionPoint[];
  /** Oldest first, as neighbouring events' labels are merged and set left to right. */
  events: readonly HistoryEvent[];
  /** The counts at the latest assessment in the range, which may be beyond the columns. */
  latest: Readonly<Record<Charted, number>>;
}) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { ref, width } = useWidth(560);
  const [picked, setPicked] = useState<number | undefined>(undefined);
  const tableId = useId();
  const labels: Record<Charted, string> = {
    needs: t("assessment.summary.column.needs"),
    strengths: t("assessment.summary.column.strengths"),
    history: t("assessment.summary.column.history"),
  };
  const latestLabels: Record<Charted, string> = {
    needs: t("assessment.history.chart.latestNeeds"),
    strengths: t("assessment.history.chart.latestStrengths"),
    history: t("assessment.history.chart.latestHistory"),
  };

  if (points.length === 0) {
    return null;
  }

  const plotHeight = HEIGHT - PLOT.top - PLOT.bottom;
  const column = width / points.length;
  const xOf = (index: number) => column * (index + 0.5);
  const max = Math.max(
    1,
    ...points.flatMap(({ counts }) => CHARTED.map((key) => counts[key])),
  );
  const step = stepFor(max);
  const top = Math.ceil(max / step) * step;
  const yOf = (count: number) =>
    PLOT.top + plotHeight - (count / top) * plotHeight;
  const ticks = Array.from(
    { length: top / step + 1 },
    (_, index) => index * step,
  );
  const pickedPoint = picked === undefined ? undefined : points[picked];

  const markers = events.reduce<Marker[]>((placed, event) => {
    const x = xOfInstant(points, xOf, event.at);

    if (x === undefined) {
      return placed;
    }

    const name =
      event.kind === "placement"
        ? event.name
        : t("assessment.history.event.assessor");
    const last = placed.at(-1);

    return last !== undefined && x - last.x < MERGE_WITHIN
      ? [...placed.slice(0, -1), { ...last, label: `${last.label} · ${name}` }]
      : [...placed, { x, label: name }];
  }, []);

  const pickNearest = (x: number) => {
    setPicked(Math.min(points.length - 1, Math.max(0, Math.floor(x / column))));
  };

  const onKeyDown = (event: ReactKeyboardEvent<SVGSVGElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }

    event.preventDefault();
    const from = picked ?? (event.key === "ArrowLeft" ? points.length : -1);
    const to = event.key === "ArrowLeft" ? from - 1 : from + 1;
    setPicked(Math.min(points.length - 1, Math.max(0, to)));
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `${PROGRESSION_COLUMNS.item} minmax(0, 1fr) ${PROGRESSION_COLUMNS.across}`,
      }}
    >
      <Box aria-hidden sx={{ position: "relative", height: HEIGHT }}>
        {ticks.map((tick) => (
          <Typography
            key={tick}
            variant="caption"
            sx={{
              position: "absolute",
              right: 12,
              top: yOf(tick),
              transform: "translateY(-50%)",
              color: "text.secondary",
              lineHeight: 1,
            }}
          >
            {tick}
          </Typography>
        ))}
      </Box>
      <Box ref={ref} sx={{ position: "relative", minWidth: 0 }}>
        <svg
          width={width}
          height={HEIGHT}
          role="group"
          tabIndex={0}
          aria-label={t("assessment.history.chart.label")}
          aria-describedby={tableId}
          onPointerMove={(event) => {
            pickNearest(
              event.clientX - event.currentTarget.getBoundingClientRect().left,
            );
          }}
          onPointerLeave={() => {
            setPicked(undefined);
          }}
          onBlur={() => {
            setPicked(undefined);
          }}
          onKeyDown={onKeyDown}
          style={{ display: "block", outlineOffset: 2 }}
        >
          {ticks.map((tick) => (
            <line
              key={tick}
              x1={0}
              x2={width}
              y1={yOf(tick)}
              y2={yOf(tick)}
              stroke={palette.divider}
              strokeWidth={1}
            />
          ))}
          {labelled(markers, width).map((marker) => (
            <g key={`${String(marker.x)}-${marker.label}`}>
              <line
                x1={marker.x}
                x2={marker.x}
                y1={PLOT.top - 6}
                y2={PLOT.top + plotHeight}
                stroke={palette.chart.event}
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              <text
                x={marker.left}
                y={LABEL_ROWS[marker.row]}
                fontSize={LABEL_FONT_SIZE}
                fontWeight={600}
                fill={palette.chart.event}
              >
                {/* Locale words are lower case to read inside a shared label, so the label's first letter is raised here. */}
                {marker.label.charAt(0).toUpperCase() + marker.label.slice(1)}
              </text>
            </g>
          ))}
          {CHARTED.map((key) => (
            <g key={key}>
              <polyline
                fill="none"
                stroke={palette.chart[key]}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                points={points
                  .map(
                    (point, index) =>
                      `${String(xOf(index))},${String(yOf(point.counts[key]))}`,
                  )
                  .join(" ")}
              />
              {points.map((point, index) => (
                <circle
                  key={point.assessmentId}
                  cx={xOf(index)}
                  cy={yOf(point.counts[key])}
                  r={3.5}
                  fill={palette.chart[key]}
                  stroke={palette.background.paper}
                  strokeWidth={1.5}
                />
              ))}
            </g>
          ))}
          {picked === undefined ? null : (
            <line
              x1={xOf(picked)}
              x2={xOf(picked)}
              y1={PLOT.top}
              y2={PLOT.top + plotHeight}
              stroke={palette.text.primary}
              strokeWidth={1}
            />
          )}
        </svg>
        {pickedPoint === undefined || picked === undefined ? null : (
          <Box
            role="status"
            sx={{
              position: "absolute",
              top: PLOT.top,
              left: Math.min(xOf(picked) + 12, Math.max(0, width - 180)),
              pointerEvents: "none",
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: 1,
              px: 1.25,
              py: 0.75,
              minWidth: 168,
            }}
          >
            <Typography
              variant="caption"
              component="p"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {SHORT_DATE.format(pickedPoint.at)}
            </Typography>
            {CHARTED.map((key) => (
              <Stack
                key={key}
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: palette.chart[key],
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.primary", flexGrow: 1 }}
                >
                  {labels[key]}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.primary", fontWeight: 600 }}
                >
                  {pickedPoint.counts[key]}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ pl: 2, pt: `${String(PLOT.top)}px` }}>
        <Typography
          variant="caption"
          component="p"
          sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
        >
          {t("assessment.history.chart.latest")}
        </Typography>
        {CHARTED.map((key) => (
          <Typography
            key={key}
            variant="caption"
            component="p"
            sx={{ color: "text.primary", lineHeight: 1.8 }}
          >
            <Box
              component="span"
              sx={{
                mr: 0.5,
                fontSize: "0.9375rem",
                fontWeight: 700,
                color: palette.chart[key],
              }}
            >
              {latest[key]}
            </Box>
            {latestLabels[key]}
          </Typography>
        ))}
      </Box>
      <Box component="table" id={tableId} sx={VISUALLY_HIDDEN}>
        <caption>{t("assessment.history.chart.label")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("assessment.history.chart.date")}</th>
            {CHARTED.map((key) => (
              <th key={key} scope="col">
                {labels[key]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.assessmentId}>
              <th scope="row">{SHORT_DATE.format(point.at)}</th>
              {CHARTED.map((key) => (
                <td key={key}>{point.counts[key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

/** The key to the chart's lines, for the heading above it. */
export const ProgressionLegend = () => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const labels: Record<Charted, string> = {
    needs: t("assessment.summary.column.needs"),
    strengths: t("assessment.summary.column.strengths"),
    history: t("assessment.summary.column.history"),
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      useFlexGap
      flexWrap="wrap"
      justifyContent="flex-end"
      aria-hidden
    >
      {CHARTED.map((key) => (
        <Stack key={key} direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 16,
              height: 2,
              bgcolor: palette.chart[key],
              borderRadius: 1,
            }}
          />
          <Typography variant="caption" sx={{ color: "text.primary" }}>
            {labels[key]}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
